import React, { useRef, useState } from "react";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { Download, Loader2 } from "lucide-react";
import { usePremisesStore, useRegionStore } from "../../../store/store";
import { getSignedUrlForS3 } from "../../../utils/s3Client";

interface GenericRCAPrintPDFProps {
    formData: any;
    incidentData: any;
    buttonClass?: string;
}

const GenericRCAPrintPDF: React.FC<GenericRCAPrintPDFProps> = ({ formData, incidentData, buttonClass }) => {
    const printRef = useRef<HTMLDivElement>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const { allPremises } = usePremisesStore();
    const { regions } = useRegionStore();

    const getPremiseName = (id: string) => {
        return allPremises.find((p: any) => p._id === id || p.premisesName === id)?.premisesName || id;
    };

    const getRegionName = (id: string) => {
        return regions.find((r: any) => r._id === id || r.regionName === id)?.regionName || id;
    };

    const handleDownloadPdf = async () => {
        if (!printRef.current) return;
        setIsGenerating(true);

        try {
            const wrapper = printRef.current;
            wrapper.style.display = "block";
            wrapper.style.position = "absolute";
            wrapper.style.left = "-9999px";
            wrapper.style.top = "0";

            // Allow DOM to update
            await new Promise((resolve) => setTimeout(resolve, 500));

            const element = wrapper.firstElementChild as HTMLElement;

            const opt = {
                margin: 1, // 10mm margin on all sides top, bottom, left, right
                filename: `RCA_Report_${incidentData?.id || "Incident"}.pdf`,
                image: { type: 'jpeg' as const, quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: 740 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
                pagebreak: { mode: ['css', 'legacy'] }
            };

            await html2pdf().set(opt).from(element).save();

            // Hide again
            wrapper.style.display = "none";
            wrapper.style.position = "";
            wrapper.style.left = "";
            wrapper.style.top = "";

        } catch (error) {
            console.error("Failed to generate PDF:", error);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={isGenerating}
                className={buttonClass || "flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md text-sm border border-gray-300 transition-colors"}
            >
                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {isGenerating ? "Generating PDF..." : "Download PDF"}
            </button>

            {/* Hidden printable content */}
            <div ref={printRef} style={{ display: "none" }}>
                <div
                    className="pdf-print-root bg-white px-2 py-4 mx-auto"
                    style={{
                        fontFamily: "Inter, sans-serif",
                        color: "#1f2937",
                        width: '740px',
                        fontSize: "11px"
                    }}
                >
                    <style>
                        {`
                            /* Scoped base styles */
                            .pdf-print-root {
                                font-family: "Inter", sans-serif;
                                color: #1f2937;
                            }
                            .pdf-print-root * {
                                box-sizing: border-box;
                            }

                            /* Page layout and breaks */
                            .pdf-print-root .page-break-avoid, 
                            .pdf-print-root tr, 
                            .pdf-print-root td, 
                            .pdf-print-root th {
                                page-break-inside: avoid !important;
                                break-inside: avoid !important;
                            }
                            .pdf-print-root .page-break-before {
                                page-break-before: always !important;
                                break-before: page !important;
                            }

                            /* Tables normalization */
                            .pdf-print-root table {
                                width: 100% !important;
                                table-layout: fixed;
                                border-collapse: collapse;
                            }
                            .pdf-print-root th, 
                            .pdf-print-root td {
                                word-wrap: break-word;
                                font-size: 11px !important;
                                padding: 6px 8px !important;
                                line-height: 1.4 !important;
                            }

                            /* Typography Scale - Normalizes inconsistent Tailwind classes */
                            .pdf-print-root h1, .pdf-print-root .text-2xl, .pdf-print-root .text-3xl {
                                font-size: 20px !important;
                                line-height: 1.2 !important;
                            }
                            .pdf-print-root h2, .pdf-print-root .text-lg {
                                font-size: 14px !important;
                                line-height: 1.3 !important;
                                letter-spacing: 0.3px;
                            }
                            .pdf-print-root p, .pdf-print-root div, .pdf-print-root span {
                                font-size: 12px;
                                line-height: 1.5;
                            }
                            .pdf-print-root .text-sm {
                                font-size: 12px !important;
                                line-height: 1.5 !important;
                            }
                            .pdf-print-root .text-xs {
                                font-size: 10px !important;
                            }

                            /* Critical: Reset for Fishbone SVG */
                            .pdf-print-root foreignObject * {
                                line-height: 1.35 !important;
                            }
                        `}
                    </style>

                    {/* Header */}
                    <div className="border-b-4 border-[#0fa9a6] pb-3 mb-4 flex justify-between items-end">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Root Cause Analysis Report</h1>
                            <p className="text-gray-500 mt-1">Incident Report #{incidentData?.id || "N/A"}</p>
                        </div>
                        <div className="text-sm font-medium bg-gray-100 px-3 py-1 pb-2 rounded text-gray-700">
                            Generated: {new Date().toLocaleDateString()}
                        </div>
                    </div>

                    {/* Section A: Introduction */}
                    <div className="mb-6 page-break-avoid">
                        <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                            A. Introduction
                        </h2>
                        <div className="grid grid-cols-2 gap-3 text-sm border p-4 rounded bg-gray-50">
                            <div><span className="font-semibold text-gray-600">Region:</span> {getRegionName(formData.region)}</div>
                            <div><span className="font-semibold text-gray-600">Premises:</span> {getPremiseName(formData.premisesName)}</div>
                            <div><span className="font-semibold text-gray-600">Incident Type:</span> {formData.incidentEventType === "Other" ? formData.incidentEventTypeOther : formData.incidentEventType}</div>
                            <div><span className="font-semibold text-gray-600">Date/Time of Event:</span> {formData.eventDateTime ? new Date(formData.eventDateTime).toLocaleString() : ""}</div>
                            <div><span className="font-semibold text-gray-600">Date of RCA:</span> {formData.rcaDoneDate}</div>
                            <div><span className="font-semibold text-gray-600">Severity:</span> <span className={`font-semibold ${formData.severity === 'High' || formData.severity === 'Critical' ? 'text-red-600' : 'text-orange-500'}`}>{formData.severity}</span></div>
                            <div><span className="font-semibold text-gray-600">Reported By:</span> {formData.reportedBy}</div>
                            <div><span className="font-semibold text-gray-600">Location:</span> {formData.location}</div>
                        </div>
                    </div>

                    {/* Section B: Team */}
                    {formData.rcaTeamMembers && formData.rcaTeamMembers.length > 0 && (
                        <div className="mb-6 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                B. RCA Team Members
                            </h2>
                            <table className="w-full text-sm border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <td className="border border-gray-300 p-2 font-semibold w-16">Sr. No</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Name</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Department</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Role</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.rcaTeamMembers.map((member: any, i: number) => (
                                        <tr key={i}>
                                            <td className="border border-gray-300 p-2 w-16">{member.srNo}</td>
                                            <td className="border border-gray-300 p-2">{member.name}</td>
                                            <td className="border border-gray-300 p-2">{member.department}</td>
                                            <td className="border border-gray-300 p-2 font-medium">{member.isLead ? <span className="text-[#0fa9a6]">Lead</span> : "Member"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Section C: Synopsis */}
                    <div className="mb-6 page-break-avoid">
                        <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                            C. Incident Synopsis & Summary
                        </h2>
                        <div className="space-y-4 text-sm mt-3">
                            <div>
                                <strong className="block text-gray-700 mb-1">Incident Synopsis:</strong>
                                <p className="bg-gray-50 p-3 rounded border">{formData.incidentSynopsis || "N/A"}</p>
                            </div>
                            <div>
                                <strong className="block text-gray-700 mb-1">Observations:</strong>
                                <p className="bg-gray-50 p-3 rounded border">{formData.observations || "N/A"}</p>
                            </div>
                            <div>
                                <strong className="block text-gray-700 mb-1">Summary:</strong>
                                <p className="bg-gray-50 p-3 rounded border">{formData.summary || "N/A"}</p>
                            </div>
                            <div>
                                <strong className="block text-gray-700 mb-1">Identified Cause:</strong>
                                <p className="bg-gray-50 p-3 rounded border">{formData.identifiedCause || "N/A"}</p>
                            </div>
                        </div>
                    </div>

                    {/* Section D: Timeline */}
                    {formData.timelineEvents && formData.timelineEvents.length > 0 && (
                        <div className="mb-6 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                D. Timeline of Events
                            </h2>
                            <table className="w-full text-sm border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <td className="border border-gray-300 p-2 font-semibold w-24">Date</td>
                                        <td className="border border-gray-300 p-2 font-semibold w-20">Time</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Event</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.timelineEvents.map((t: any, i: number) => (
                                        <tr key={i}>
                                            <td className="border border-gray-300 p-2 whitespace-nowrap w-24">{t.date}</td>
                                            <td className="border border-gray-300 p-2 whitespace-nowrap w-20">{t.time}</td>
                                            <td className="border border-gray-300 p-2">{t.event}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Section E: Witnesses */}
                    {formData.witnessStatements && formData.witnessStatements.length > 0 && (
                        <div className="mb-6 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                E. Witness Statements
                            </h2>
                            <div className="space-y-3">
                                {formData.witnessStatements.map((w: any, i: number) => (
                                    <div key={i} className="border p-4 rounded bg-gray-50 text-sm page-break-avoid">
                                        <div className="font-semibold text-gray-800 mb-1">{w.witnessName} <span className="text-gray-500 font-normal">({w.contactInfo})</span></div>
                                        <div className="italic text-gray-700 border-l-2 pl-3 mt-2 border-gray-300">"{w.statement}"</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Section F: Consequences */}
                    {formData.consequences && formData.consequences.length > 0 && (
                        <div className="mb-6 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                F. Consequences
                            </h2>
                            <table className="w-full text-sm border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <td className="border border-gray-300 p-2 font-semibold">Type</td>
                                        <td className="border border-gray-300 p-2 font-semibold text-center w-24">Applicable</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Remark</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.consequences.map((c: any, i: number) => (
                                        <tr key={i} className={c.isYes ? "bg-red-50" : ""}>
                                            <td className="border border-gray-300 p-2">{c.type}</td>
                                            <td className="border border-gray-300 p-2 text-center text-red-600 font-bold">{c.isYes ? "Yes" : "No"}</td>
                                            <td className="border border-gray-300 p-2">{c.remark}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Section G: Fishbone / RCA Analysis */}
                    {formData.rcaMethod && formData.fishboneAnalysis && (() => {
                        const CATS = [
                            { key: 'man',        label: 'Man',        color: '#dc2626', bg: '#fef2f2', border: '#fca5a5',
                              desc: 'Human factors: lack of training, communication, awareness, lack of adequate human power, arson etc.' },
                            { key: 'machine',    label: 'Machine',    color: '#16a34a', bg: '#f0fdf4', border: '#86efac',
                              desc: 'Faulty machinery or unavailability of redundant machineries.' },
                            { key: 'management', label: 'Management', color: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd',
                              desc: 'Expenditure & Budget problems.' },
                            { key: 'material',   label: 'Material',   color: '#2563eb', bg: '#eff6ff', border: '#93c5fd',
                              desc: 'Lack of materials, inventories, safety devices / equipment or poor storage practices.' },
                            { key: 'system',     label: 'System',     color: '#ea580c', bg: '#fff7ed', border: '#fdba74',
                              desc: 'Lack of periodical inspection, non-compliance to standards & no preventive maintenance activity.' },
                        ] as const;
                        const fb = formData.fishboneAnalysis;

                        return (
                            <div style={{ marginBottom: '24px', borderTop: '2px dashed #e2e8f0', paddingTop: '24px', pageBreakBefore: 'always', breakBefore: 'page' }}>
                                <h2 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px', background: 'rgba(15,169,166,0.08)', padding: '6px 12px', borderRadius: '4px', color: '#0fa9a6', borderLeft: '4px solid #0fa9a6' }}>
                                    G. Root Cause Analysis ({formData.rcaMethod})
                                </h2>

                                {/* Root cause highlight */}
                                {formData.rootCauseDescription && (
                                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderLeft: '5px solid #dc2626', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '11px' }}>
                                        <span style={{ fontWeight: 700, color: '#991b1b', marginRight: '6px' }}>Root Cause:</span>
                                        <span style={{ color: '#1e293b' }}>{formData.rootCauseDescription}</span>
                                    </div>
                                )}

                                {/* Fishbone category cards — 2 per row */}
                                {/* ── Inline SVG Fishbone Diagram ── */}
                                {(() => {
                                    // Same geometry/colours as the UI component
                                    const SPINE_Y = 240;
                                    const MAX_F   = 4;
                                    const PILL_W  = 126;
                                    const PILL_H  = 26;
                                    const F_STEP  = 17;
                                    const trunc = (s: string, n: number) =>
                                        s && s.length > n ? s.slice(0, n) + '…' : (s || '');

                                    const SVGCATS = [
                                        { key: 'man',        label: 'Man',        color: '#dc2626', spineX: 175, tipCX: 80,  tipY: 14,  isTop: true  },
                                        { key: 'machine',    label: 'Machine',    color: '#16a34a', spineX: 380, tipCX: 280, tipY: 14,  isTop: true  },
                                        { key: 'management', label: 'Management', color: '#7c3aed', spineX: 580, tipCX: 480, tipY: 14,  isTop: true  },
                                        { key: 'material',   label: 'Material',   color: '#2563eb', spineX: 280, tipCX: 175, tipY: 466, isTop: false },
                                        { key: 'system',     label: 'System',     color: '#ea580c', spineX: 480, tipCX: 375, tipY: 466, isTop: false },
                                    ] as const;

                                    const fb = formData.fishboneAnalysis;

                                    return (
                                        <svg
                                            viewBox="0 0 960 480"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{ width: '100%', height: 'auto', display: 'block', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px' }}
                                        >
                                            <defs>
                                                <marker id="pdf-fb-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                                    <polygon points="0 0,10 3.5,0 7" fill="#374151" />
                                                </marker>
                                            </defs>

                                            {/* Background stripes (subtle) */}
                                            <rect x={0} y={0} width={960} height={480} fill="#f8fafc" />

                                            {/* ── Spine ── */}
                                            <line x1={20} y1={SPINE_Y} x2={748} y2={SPINE_Y}
                                                stroke="#1e293b" strokeWidth={4.5} strokeLinecap="round"
                                                markerEnd="url(#pdf-fb-arrow)" />

                                            {/* ── Fish head (ROOT CAUSE) ── */}
                                            <rect x={760} y={175} width={188} height={130} rx={13} fill="#0f766e" stroke="#042f2e" strokeWidth={2} />
                                            <text x={854} y={205} textAnchor="middle" fontSize={11} fill="#ffffff" fontWeight="bold" letterSpacing="0.8">
                                                ROOT CAUSE
                                            </text>
                                            {/* Root cause text — up to 3 lines of 22 chars */}
                                            {(formData.rootCauseDescription || 'Not identified').match(/.{1,22}/g)?.slice(0, 3).map((line: string, li: number) => (
                                                <text key={li} x={854} y={225 + li * 16} textAnchor="middle" fontSize={9.5} fill="#ccfbf1" fontWeight={600}>
                                                    {line}
                                                </text>
                                            ))}

                                            {/* ── Categories ── */}
                                            {SVGCATS.map(cat => {
                                                const factors: string[] = fb[cat.key] || [];
                                                const visible = factors.slice(0, MAX_F);
                                                const extra   = factors.length - MAX_F;

                                                const pillTop  = cat.isTop ? cat.tipY : cat.tipY - PILL_H - 4;
                                                const pillLeft = cat.tipCX - PILL_W / 2;
                                                // Branch tip connects to edge of pill
                                                const branchTipY = cat.isTop ? pillTop + PILL_H : pillTop;
                                                // Factor text starts just past the pill
                                                const factorBaseY = cat.isTop
                                                    ? pillTop + PILL_H + F_STEP
                                                    : pillTop - F_STEP;

                                                return (
                                                    <g key={cat.key}>
                                                        {/* Diagonal branch */}
                                                        <line
                                                            x1={cat.spineX} y1={SPINE_Y}
                                                            x2={cat.tipCX}  y2={branchTipY}
                                                            stroke={cat.color} strokeWidth={2.5} strokeLinecap="round" opacity={0.85}
                                                        />
                                                        {/* Dot at spine junction */}
                                                        <circle cx={cat.spineX} cy={SPINE_Y} r={4.5} fill={cat.color} />

                                                        {/* Category pill */}
                                                        <rect x={pillLeft} y={pillTop} width={PILL_W} height={PILL_H} rx={13} fill={cat.color} opacity={0.93} />
                                                        <text x={cat.tipCX} y={pillTop + 17} textAnchor="middle" fontSize={11} fontWeight="bold" fill="white">
                                                            {cat.label}
                                                        </text>

                                                        {/* Factor count badge */}
                                                        {factors.length > 0 && (
                                                            <>
                                                                <circle cx={pillLeft + PILL_W + 13} cy={pillTop + PILL_H / 2} r={10} fill="white" stroke={cat.color} strokeWidth={1.5} />
                                                                <text x={pillLeft + PILL_W + 13} y={pillTop + PILL_H / 2 + 4} textAnchor="middle" fontSize={9} fontWeight="700" fill={cat.color}>
                                                                    {factors.length}
                                                                </text>
                                                            </>
                                                        )}

                                                        {/* Factor text bubbles */}
                                                        {visible.map((f, i) => {
                                                            const fy = cat.isTop
                                                                ? factorBaseY + i * F_STEP
                                                                : factorBaseY - i * F_STEP;
                                                            const label = trunc(f, 20);
                                                            return (
                                                                <g key={i}>
                                                                    <rect x={cat.tipCX - 62} y={fy - 11} width={124} height={14} rx={3}
                                                                        fill="white" stroke={`${cat.color}50`} strokeWidth={0.8} />
                                                                    <text x={cat.tipCX} y={fy} textAnchor="middle" fontSize={9}
                                                                        fill={f ? '#1e293b' : '#94a3b8'} fontStyle={f ? 'normal' : 'italic'}>
                                                                        {label || '(empty)'}
                                                                    </text>
                                                                </g>
                                                            );
                                                        })}

                                                        {/* +N more indicator */}
                                                        {extra > 0 && (
                                                            <text
                                                                x={cat.tipCX}
                                                                y={cat.isTop
                                                                    ? factorBaseY + visible.length * F_STEP + 4
                                                                    : factorBaseY - visible.length * F_STEP - 4}
                                                                textAnchor="middle" fontSize={9} fill={cat.color} fontStyle="italic" opacity={0.85}>
                                                                +{extra} more
                                                            </text>
                                                        )}
                                                    </g>
                                                );
                                            })}
                                        </svg>
                                    );
                                })()}

                                {/* Fishbone factor cards — 2 per row */}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '6px' }}>
                                    {CATS.map(cat => {
                                        const factors: string[] = fb[cat.key] || [];
                                        return (
                                            <div key={cat.key} style={{ border: `1px solid ${cat.border}`, borderLeft: `5px solid ${cat.color}`, borderRadius: '6px', background: cat.bg, padding: '10px 12px', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                                {/* Category header */}
                                                <div style={{ fontWeight: 700, fontSize: '11px', color: cat.color, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                                                    {cat.label}
                                                    <span style={{ marginLeft: 'auto', background: cat.color, color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '10px', fontWeight: 700 }}>
                                                        {factors.length}
                                                    </span>
                                                </div>
                                                {/* Description */}
                                                <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '8px', fontStyle: 'italic', lineHeight: '1.4' }}>
                                                    {cat.desc}
                                                </div>
                                                {/* Factors list */}
                                                {factors.length === 0 ? (
                                                    <div style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>No factors added</div>
                                                ) : (
                                                    <ul style={{ margin: 0, paddingLeft: '14px', listStyleType: 'disc' }}>
                                                        {factors.map((f, i) => (
                                                            <li key={i} style={{ fontSize: '10px', color: f ? '#1e293b' : '#94a3b8', fontStyle: f ? 'normal' : 'italic', marginBottom: '3px', lineHeight: '1.4' }}>
                                                                {f || '(empty)'}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}


                    {formData.rcaMethod && formData.rcaMethod !== "Fishbone" && formData.whyWhyAnalysis && formData.whyWhyAnalysis.length > 0 && (
                        <div className="mb-6 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                G. Why-Why Analysis
                            </h2>
                            <div className="space-y-3 pl-4 border-l-2 border-[#0fa9a6]">
                                {formData.whyWhyAnalysis.map((w: any, i: number) => (
                                    <div key={i} className="bg-gray-50 p-3 rounded page-break-avoid">
                                        <div className="font-semibold text-[#0fa9a6] text-sm">Why #{i + 1}</div>
                                        <div className="font-medium text-gray-800 text-sm mt-1">Q: {w.question}</div>
                                        <div className="text-gray-600 text-sm mt-1">A: {w.answer}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Section H: Contributing Factors */}
                    {formData.contributingFactors && formData.contributingFactors.filter((f: any) => f.isApplicable).length > 0 && (
                        <div className="mb-6 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                H. Applicable Contributing Factors
                            </h2>
                            <table className="w-full text-sm border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <td className="border border-gray-300 p-2 font-semibold">Factor</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Observation</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.contributingFactors.filter((f: any) => f.isApplicable).map((f: any, i: number) => (
                                        <tr key={i}>
                                            <td className="border border-gray-300 p-2 w-1/2">{f.factor}</td>
                                            <td className="border border-gray-300 p-2 font-medium">{f.observation}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Section I & J: Root Cause & Conclusion */}
                    <div className="mb-6 page-break-avoid">
                        <h2 className="text-lg font-semibold mb-3 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                            I & J. Root Cause Identified & Conclusion
                        </h2>
                        <div className="bg-gray-50 border p-4 rounded text-sm mb-3 page-break-avoid">
                            <strong className="block text-gray-800 mb-2 border-b pb-1">Root Cause:</strong>
                            <p className="font-medium text-red-700">{formData.rootCauseDescription || "Not explicitly identified."}</p>
                        </div>

                        <div className="bg-gray-50 border p-4 rounded text-sm page-break-avoid">
                            <strong className="block text-gray-800 mb-2 border-b pb-1">Conclusion:</strong>
                            <p className="text-gray-700 whitespace-pre-wrap">{formData.conclusion || "No conclusion provided."}</p>
                        </div>
                    </div>

                    {/* Section K: Corrective Actions */}
                    {formData.correctiveActionPlans && formData.correctiveActionPlans.length > 0 && (
                        <div className="mb-6 mt-2 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                K. Corrective Action Plans
                            </h2>
                            <table className="w-full text-sm border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <td className="border border-gray-300 p-2 font-semibold w-1/4">Root Cause / Factor</td>
                                        <td className="border border-gray-300 p-2 font-semibold w-1/3">Corrective Action</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Responsible</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Deadline</td>
                                        <td className="border border-gray-300 p-2 font-semibold">Status</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.correctiveActionPlans.map((c: any, i: number) => (
                                        <tr key={i}>
                                            <td className="border border-gray-300 p-2">{c.rootCauseOrContributingFactor}</td>
                                            <td className="border border-gray-300 p-2">{c.correctiveAction}</td>
                                            <td className="border border-gray-300 p-2 font-medium">{c.responsiblePersonOrTeam}</td>
                                            <td className="border border-gray-300 p-2">{c.deadline}</td>
                                            <td className="border border-gray-300 p-2"><span className={`px-2 py-1 rounded text-xs font-bold ${c.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{c.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Section L: Preventive Actions */}
                    {formData.preventiveActions && formData.preventiveActions.length > 0 && (
                        <div className="mb-6 page-break-avoid">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                L. Preventive Actions
                            </h2>
                            <table className="w-full text-sm border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <td className="border border-gray-300 p-2 font-semibold w-1/4">Sr. No</td>
                                        <td className="border border-gray-300 p-2 font-semibold w-1/2">Preventive Action</td>
                                        <td className="border border-gray-300 p-2 font-semibold w-1/4">Responsible</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.preventiveActions.map((c: any, i: number) => (
                                        <tr key={i}>
                                            <td className="border border-gray-300 p-2 text-center">{c.srNo || i + 1}</td>
                                            <td className="border border-gray-300 p-2">{c.preventiveAction}</td>
                                            <td className="border border-gray-300 p-2">{c.responsiblePersonOrTeam}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Section M: Image Attachments */}
                    {formData.attachments && formData.attachments.length > 0 && (
                        <div className="mb-6 mt-2 page-break-before">
                            <h2 className="text-lg font-semibold mb-2 bg-[#0fa9a6]/10 px-3 py-1.5 rounded text-[#0fa9a6] border-l-4 border-[#0fa9a6]">
                                M. Evidences / Attachments
                            </h2>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                {formData.attachments.map((att: any, index: number) => {
                                    const rawUrl = typeof att === "string" ? att : (att.url || "");
                                    if (!rawUrl) return null;

                                    const lowerUrl = rawUrl.toLowerCase();
                                    const isImage = lowerUrl.includes(".png") || lowerUrl.includes(".jpg") || lowerUrl.includes(".jpeg") || lowerUrl.includes(".gif");

                                    if (isImage) {
                                        return (
                                            <div key={index} className="border p-2 rounded bg-gray-50 flex flex-col items-center page-break-avoid">
                                                <img
                                                    src={getSignedUrlForS3(rawUrl)}
                                                    alt={`Attachment ${index + 1}`}
                                                    className="max-w-full max-h-[300px] object-contain"
                                                    crossOrigin="anonymous"
                                                />
                                                <span className="text-xs text-gray-500 mt-2 mb-1 block w-full text-center truncate">
                                                    {att.filename || att.name || `Image ${index + 1}`}
                                                </span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};

export default GenericRCAPrintPDF;
